import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { API_URL } from '../../config/api';
import { addNotification } from '../../store/slices/uiSlice';
import { sendMessage, setCurrentChannel, updateMessage } from '../../store/slices/chatSlice';
import PresignedImage from '../common/PresignedImage';
import FileHoverPreview from '../common/FileHoverPreview';
import FileNameWithExtension from '../common/FileNameWithExtension';
import FileTypeIcon from '../common/FileTypeIcon';
import ImageViewerModal from './ImageViewerModal';
import ForwardMessageModal from './ForwardMessageModal';
import GroupImageComposer from './GroupImageComposer';
import DraggableFileThumb from './DraggableFileThumb';
import DraggableLinkThumb from './DraggableLinkThumb';
import LinkHoverPreview from '../common/LinkHoverPreview';
import { isLinkAttachment, canGroupSharedAttachments, downloadAttachmentsAsZip, splitFileNameParts, composeFileName, getSharedEntryCategory, SHARED_FILE_FILTER_KEYS, createDefaultSharedFileFilters, sharedEntryMatchesLikedFilter, linkDomainFromUrl, getLinkDisplayTitle, normalizeLinkUrlForDedup, decodeLinkHref, downloadAttachment, collapseDuplicatedFileName, canPreviewInApp } from '../../utils/fileUtils';
import { fileForUpload } from '../../utils/uploadFilename';
import {
  isSharedAttachmentDrag,
  readSharedAttachmentDragData,
  SHARED_FILES_ROOT_DROP,
  isRootDropTarget,
  resolveAttachmentId,
  resolvePayloadAttachmentId,
  buildMoveRequestBody,
  isLinkDragPayload,
  createAttachmentDragPayload,
} from '../../utils/sharedFilesDrag';
import { collectLocalChannelMessages, isSharedResourceMessage } from '../../realm/offlineLookups';
import { getRealmDoc, realmAvailable, upsertRealmDocs } from '../../realm';
import AttachmentLikeHeart from './AttachmentLikeHeart';
import { BookmarkIcon } from './SidebarIcons';
import { saveMessage, unsaveMessage, fetchSavedMessagesByChannel, fetchAllSavedMessages } from '../../store/slices/savedMessageSlice';

const RESOURCES_PAGE_SIZE = 50;

const parseFilesResponse = (responseData) => {
  if (responseData && !Array.isArray(responseData) && responseData.content !== undefined) {
    const messages = Array.isArray(responseData.content) ? responseData.content : [];
    return {
      messages,
      last: Boolean(responseData.last) || messages.length < RESOURCES_PAGE_SIZE,
    };
  }
  const messages = Array.isArray(responseData) ? responseData : [];
  return { messages, last: true };
};

const mergeFileEntries = (prev, incoming) => {
  const byId = new Map();
  [...(prev || []), ...(incoming || [])].forEach((entry) => {
    if (entry?.id) byId.set(String(entry.id), entry);
  });
  return Array.from(byId.values()).sort(
    (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
  );
};

const keepNamedGroupsMissingFromNext = (prev, next) => {
  const nextIds = new Set((next || []).map((entry) => String(entry.id)));
  const extras = (prev || []).filter(
    (entry) => entry?.type === 'group' && entry.resourceGroupId && !nextIds.has(String(entry.id))
  );
  return extras.length ? mergeFileEntries(extras, next) : next;
};

const resourceBookmarkBtnClass =
  'flex-shrink-0 p-1.5 rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors';

const ResourceBookmarkButton = ({ messageId, bookmarked, busy, onToggle, compact = false }) => {
  if (!messageId) return null;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!busy) onToggle(messageId);
      }}
      disabled={busy}
      className={`${
        compact
          ? 'p-0.5 rounded text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-300'
          : resourceBookmarkBtnClass
      } ${
        bookmarked
          ? compact
            ? 'text-indigo-600 dark:text-indigo-300'
            : 'text-indigo-600 dark:text-indigo-300 border-indigo-200 dark:border-indigo-700'
          : compact
            ? ''
            : 'text-gray-700 dark:text-gray-200'
      } disabled:opacity-50 flex-shrink-0`}
      title={bookmarked ? 'Remove bookmark' : 'Bookmark this'}
      aria-label={bookmarked ? 'Remove bookmark' : 'Bookmark this'}
      aria-pressed={bookmarked}
    >
      <BookmarkIcon className={compact ? 'w-4 h-4' : 'w-4 h-4'} filled={bookmarked} />
    </button>
  );
};

const SharedFilesList = ({ channelId, isDirectMessage, refreshRef }) => {
  const dispatch = useDispatch();
  const { user: currentUser } = useSelector((state) => state.auth);
  const savedMessageIds = useSelector((state) => state.savedMessages.savedMessageIds || []);
  const [bookmarkBusyId, setBookmarkBusyId] = useState(null);
  const { channels = [], directMessages = [] } = useSelector((state) => state.channels);
  const reduxChannelMessages = useSelector((state) =>
    channelId ? (state.chat.messages[channelId] || []) : []
  );
  const localMessagesRef = useRef([]);
  localMessagesRef.current = reduxChannelMessages;
  const [resolvedIsDm, setResolvedIsDm] = useState(Boolean(isDirectMessage));

  const isDmContext = useMemo(() => {
    if (isDirectMessage || resolvedIsDm) return true;
    const match = [...directMessages, ...channels].find((c) => c?.id === channelId);
    return match?.type === 'direct' || match?.type === 'DIRECT_MESSAGE';
  }, [isDirectMessage, resolvedIsDm, channelId, channels, directMessages]);

  const sameUser = (a, b) => a != null && b != null && String(a) === String(b);
  const [files, setFiles] = useState([]);
  const filesRef = useRef([]);
  filesRef.current = files;
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [renamingFile, setRenamingFile] = useState(null);
  const [newFileName, setNewFileName] = useState('');
  const [viewingImage, setViewingImage] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [downloadingGroupId, setDownloadingGroupId] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreFiles, setHasMoreFiles] = useState(true);
  const [filesPage, setFilesPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [savingGroupId, setSavingGroupId] = useState(null);
  const [dropTargetGroupId, setDropTargetGroupId] = useState(null);
  const [isRootDropTarget, setIsRootDropTarget] = useState(false);
  const [renamingLink, setRenamingLink] = useState(null);
  const [newLinkTitle, setNewLinkTitle] = useState('');
  const [typeFilters, setTypeFilters] = useState(createDefaultSharedFileFilters);

  useEffect(() => {
    setTypeFilters((prev) => {
      let changed = false;
      const next = { ...prev };
      SHARED_FILE_FILTER_KEYS.forEach((key) => {
        if (next[key] === undefined) {
          next[key] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, []);
  const [isDraggingShared, setIsDraggingShared] = useState(false);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [showCreateGroupInput, setShowCreateGroupInput] = useState(false);
  const [createGroupNameInput, setCreateGroupNameInput] = useState('');
  const [renamingGroupId, setRenamingGroupId] = useState(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [showForwardGroupModal, setShowForwardGroupModal] = useState(false);
  const [groupToForward, setGroupToForward] = useState(null);
  const [forwardingGroup, setForwardingGroup] = useState(false);
  const [deletingLinkId, setDeletingLinkId] = useState(null);
  const fileInputRef = useRef(null);
  const activeGroupRef = useRef(null);
  const rootDropHandledRef = useRef(false);
  const filesLoadedFromApiRef = useRef(false);

  // Keep Shared Resources in sync when likes change via chat / viewer (Redux).
  useEffect(() => {
    if (!channelId || !Array.isArray(reduxChannelMessages) || reduxChannelMessages.length === 0) return;
    const byMessageId = new Map();
    reduxChannelMessages.forEach((msg) => {
      const id = msg?.id || msg?._id;
      if (id) byMessageId.set(String(id), msg);
    });
    setFiles((prev) => {
      let changed = false;
      const next = prev.map((entry) => {
        const messageId = entry.messageId || (entry.type !== 'group' && entry.type !== 'link' ? entry.id : null);
        if (!messageId) return entry;
        const msg = byMessageId.get(String(messageId));
        if (!msg) return entry;
        const sourceAtts = [
          ...(Array.isArray(msg.attachments) ? msg.attachments : []),
          ...(Array.isArray(msg.timelineAttachments) ? msg.timelineAttachments : []),
        ];
        if (!sourceAtts.length) return entry;

        const likeMap = new Map();
        sourceAtts.forEach((att) => {
          const aid = resolveAttachmentId(att);
          if (aid && Array.isArray(att.likedByUserIds)) {
            likeMap.set(String(aid), att.likedByUserIds);
          }
        });
        if (!likeMap.size) return entry;

        if (entry.type === 'group') {
          let groupChanged = false;
          const attachments = (entry.attachments || []).map((att) => {
            const aid = resolveAttachmentId(att);
            if (!aid || !likeMap.has(String(aid))) return att;
            const likedByUserIds = likeMap.get(String(aid));
            const prevIds = Array.isArray(att.likedByUserIds) ? att.likedByUserIds.join(',') : '';
            const nextIds = likedByUserIds.join(',');
            if (prevIds === nextIds) return att;
            groupChanged = true;
            return { ...att, likedByUserIds };
          });
          if (!groupChanged) return entry;
          changed = true;
          return { ...entry, attachments };
        }

        if (entry.type === 'link') return entry;
        const aid = resolveAttachmentId(entry);
        if (!aid || !likeMap.has(String(aid))) return entry;
        const likedByUserIds = likeMap.get(String(aid));
        const prevIds = Array.isArray(entry.likedByUserIds) ? entry.likedByUserIds.join(',') : '';
        if (prevIds === likedByUserIds.join(',')) return entry;
        changed = true;
        return { ...entry, likedByUserIds };
      });
      return changed ? next : prev;
    });
  }, [channelId, reduxChannelMessages]);
  
  const MAX_FILE_SIZE = 20 * 1024 * 1024;

  const canEditGroup = (entry) =>
    isDmContext || sameUser(entry.senderId, currentUser?.id);

  const canEditFile = (file) =>
    isDmContext || sameUser(file.senderId, currentUser?.id);

  const canEditLink = (link) =>
    isDmContext || sameUser(link.senderId, currentUser?.id);

  useEffect(() => {
    if (channelId) {
      dispatch(fetchSavedMessagesByChannel({ channelId, page: 0, size: 50, append: false }));
    } else {
      dispatch(fetchAllSavedMessages());
    }
  }, [dispatch, channelId]);

  const isResourceBookmarked = (messageId) =>
    Boolean(messageId) && savedMessageIds.some((id) => String(id) === String(messageId));

  const handleToggleResourceBookmark = async (messageId) => {
    if (!messageId || bookmarkBusyId) return;
    const bookmarked = isResourceBookmarked(messageId);
    try {
      setBookmarkBusyId(messageId);
      if (bookmarked) {
        await dispatch(unsaveMessage(messageId)).unwrap();
        dispatch(addNotification({ type: 'success', message: 'Bookmark removed' }));
      } else {
        await dispatch(saveMessage(messageId)).unwrap();
        dispatch(addNotification({ type: 'success', message: 'Bookmarked' }));
      }
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error?.message || error || 'Failed to update bookmark',
      }));
    } finally {
      setBookmarkBusyId(null);
    }
  };

  const normalizeAttachment = (attachment) => ({
    ...attachment,
    id: attachment?.id || attachment?._id || null,
    fileName: collapseDuplicatedFileName(attachment?.fileName),
    fileUrl: decodeLinkHref(attachment?.fileUrl) || attachment?.fileUrl,
  });

  const sharedFileKey = (file) => {
    if (file?.id) return String(file.id);
    return `${file?.messageId || 'msg'}::${file?.fileUrl || file?.fileName || 'file'}`;
  };

  const linkKey = (link) => `link-${link?.messageId || link?.id || 'unknown'}`;

  const findGroupEntryByMessageId = (messageId) =>
    files.find((f) => f.type === 'group' && f.messageId === messageId);

  const findAttachmentIndex = (attachments, attachmentId) =>
    attachments.findIndex((a) => resolveAttachmentId(a) === attachmentId);

  const buildLinkEntry = (msg) => {
    const preview = msg.linkPreview;
    const url = decodeLinkHref(preview?.url);
    if (!url || preview?.hiddenFromSharedResources) return null;

    const domain = preview?.domain || linkDomainFromUrl(url);
    return {
      type: 'link',
      id: `link-${msg.id}`,
      url,
      title: preview?.title || url,
      customTitle: preview?.customTitle,
      description: preview?.description,
      imageUrl: preview?.imageUrl,
      siteName: preview?.siteName || domain,
      domain,
      messageId: msg.id,
      senderId: msg.senderId,
      senderName: msg.senderName,
      createdAt: msg.createdAt,
    };
  };

  const buildFileEntries = (messages) => {
    const entries = [];

    messages.forEach((msg) => {
      const attachments = msg.attachments || [];
      const groupName = msg.attachmentGroupName?.trim();
      const resourceGroupId = msg.resourceGroupId || null;

      const isNamedResourceGroup = Boolean(resourceGroupId && groupName);

      if (isNamedResourceGroup || ((groupName || resourceGroupId) && canGroupSharedAttachments(attachments))) {
        entries.push({
          type: 'group',
          id: resourceGroupId ? `group-${resourceGroupId}` : `group-${msg.id}`,
          resourceGroupId,
          groupName,
          attachments: attachments.map((attachment) =>
            normalizeAttachment({
              ...attachment,
              messageId: msg.id,
              channelId: msg.channelId,
              senderId: msg.senderId,
              senderName: msg.senderName,
              createdAt: msg.createdAt,
            })
          ),
          messageId: msg.id,
          channelId: msg.channelId,
          senderId: msg.senderId,
          senderName: msg.senderName,
          createdAt: msg.createdAt,
        });
      } else {
        attachments.forEach((attachment) => {
          entries.push({
            ...normalizeAttachment(attachment),
            type: 'single',
            id: attachment.id || attachment._id,
            messageId: msg.id,
            channelId: msg.channelId,
            senderId: msg.senderId,
            senderName: msg.senderName,
            createdAt: msg.createdAt,
            });
          });
        }

      const linkEntry = buildLinkEntry(msg);
      if (linkEntry) {
        entries.push(linkEntry);
      }
    });

    entries.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    const seenRootLinkUrls = new Set();
    return entries.filter((entry) => {
      if (entry.type !== 'link') return true;
      const key = normalizeLinkUrlForDedup(entry.url);
      if (!key || seenRootLinkUrls.has(key)) return false;
      seenRootLinkUrls.add(key);
      return true;
    });
  };

  const fetchFiles = useCallback(async ({ force = false, silent = false } = {}) => {
    if (!channelId) return;
    const showSpinner = !silent && filesRef.current.length === 0;
    if (showSpinner) {
      setLoading(true);
    }
    if (!silent) {
      setError(null);
    }

    const applyLocalFiles = async () => {
      const localMessages = await collectLocalChannelMessages(channelId, localMessagesRef.current);
      const fileMessages = localMessages.filter(isSharedResourceMessage);
      if (fileMessages.length) {
        const fromLocal = buildFileEntries(fileMessages);
        setFiles((prev) => keepNamedGroupsMissingFromNext(prev, fromLocal));
        return true;
      }
      return false;
    };

    const online = typeof navigator === 'undefined' || navigator.onLine !== false;

    if (!force) {
      const hadLocal = await applyLocalFiles();
      if (hadLocal || (await realmAvailable())) {
        if (showSpinner) setLoading(false);
      } else if (!online) {
        setHasMoreFiles(false);
        if (showSpinner) setLoading(false);
        return;
      }
      if (!online) {
        setHasMoreFiles(false);
        if (showSpinner) setLoading(false);
        return;
      }
    } else if (!online) {
      await applyLocalFiles();
      setHasMoreFiles(false);
      if (showSpinner) setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/messages/channel/${channelId}/files`, {
        params: { page: 0, size: RESOURCES_PAGE_SIZE },
        headers: { Authorization: `Bearer ${token}` },
      });
      const { messages, last } = parseFilesResponse(response.data);
      const incoming = buildFileEntries(messages);
      setFiles((prev) => (force ? incoming : mergeFileEntries(prev, incoming)));
      setFilesPage(0);
      filesLoadedFromApiRef.current = true;
      setHasMoreFiles(!last);
      setError(null);
    } catch (err) {
      console.error('Error fetching files:', err);
      await applyLocalFiles();
      setError(null);
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [channelId]);

  const handleRefreshFiles = useCallback(async () => {
    if (!channelId || loading || refreshing) return;
    setRefreshing(true);
    try {
      await fetchFiles({ force: true, silent: true });
    } finally {
      setRefreshing(false);
    }
  }, [channelId, fetchFiles, loading, refreshing]);

  const handleLoadMore = useCallback(async () => {
    if (!channelId || loadingMore || !hasMoreFiles) return;
    setLoadingMore(true);
    try {
      const token = localStorage.getItem('token');
      const nextPage = filesLoadedFromApiRef.current ? filesPage + 1 : 0;
      const response = await axios.get(`${API_URL}/messages/channel/${channelId}/files`, {
        params: { page: nextPage, size: RESOURCES_PAGE_SIZE },
        headers: { Authorization: `Bearer ${token}` },
      });
      const { messages, last } = parseFilesResponse(response.data);
      const incoming = buildFileEntries(messages);
      setFiles((prev) => mergeFileEntries(prev, incoming));
      setFilesPage(nextPage);
      filesLoadedFromApiRef.current = true;
      setHasMoreFiles(!last && messages.length >= RESOURCES_PAGE_SIZE);
    } catch (err) {
      console.error('Error loading more files:', err);
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: err?.response?.data?.error || err?.message || 'Failed to load more resources',
      }));
    } finally {
      setLoadingMore(false);
    }
  }, [channelId, dispatch, filesPage, hasMoreFiles, loadingMore]);

  useEffect(() => {
    if (!refreshRef) return undefined;
    refreshRef.current = handleRefreshFiles;
    return () => {
      if (refreshRef.current === handleRefreshFiles) refreshRef.current = null;
    };
  }, [handleRefreshFiles, refreshRef]);

  // Fetch files immediately on channel change
  useEffect(() => {
    setSearchQuery('');
    setExpandedGroups({});
    setRenamingFile(null);
    setNewFileName('');
    setRenamingLink(null);
    setNewLinkTitle('');
    setIsRootDropTarget(false);
    setIsDraggingShared(false);
    setShowCreateGroupInput(false);
    setCreateGroupNameInput('');
    setTypeFilters(createDefaultSharedFileFilters());
    setFilesPage(0);
    setHasMoreFiles(true);
    setLoadingMore(false);
    filesLoadedFromApiRef.current = false;
    setFiles([]);
    setLoading(true);
    fetchFiles();
  }, [channelId, fetchFiles]);

  useEffect(() => {
    if (isDirectMessage) {
      setResolvedIsDm(true);
      return undefined;
    }
    const match = [...directMessages, ...channels].find((c) => c?.id === channelId);
    if (match?.type === 'direct' || match?.type === 'DIRECT_MESSAGE') {
      setResolvedIsDm(true);
      return undefined;
    }
    if (!channelId) {
      setResolvedIsDm(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const local = await getRealmDoc('Channel', channelId);
        const type = local?.type || match?.type;
        if (!cancelled) {
          setResolvedIsDm(type === 'direct' || type === 'DIRECT_MESSAGE');
        }
      } catch {
        if (!cancelled) setResolvedIsDm(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [channelId, isDirectMessage, channels, directMessages]);

  const handleRename = async (file) => {
    if (!newFileName.trim()) return;
    await handleRenameAttachment(file, newFileName);
    setRenamingFile(null);
  };

  const handleRenameAttachment = async (attachment, baseName) => {
    const isLink = isLinkAttachment(attachment);
    const trimmed = isLink
      ? (baseName || '').trim()
      : composeFileName((baseName || '').trim(), splitFileNameParts(attachment.fileName).extension);
    if (!trimmed) return;
    if (isLink) {
      const currentTitle = getLinkDisplayTitle({
        customTitle: attachment.linkCustomTitle,
        title: attachment.fileName,
        url: attachment.fileUrl,
      });
      if (trimmed === currentTitle) return;
    } else if (trimmed === attachment.fileName) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const attachmentId = resolveAttachmentId(attachment);
      if (!attachmentId) return;
      await axios.patch(
        `${API_URL}/messages/${attachment.messageId}/attachments/${attachmentId}/rename`,
        { newFileName: trimmed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchFiles({ force: true, silent: true });
    } catch (err) {
      console.error('Error renaming file:', err);
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to rename file',
      }));
      throw err;
    }
  };

  const handleRenameLink = async (link, newTitle) => {
    const trimmed = (newTitle || '').trim();
    if (!trimmed) return;
    if (trimmed === getLinkDisplayTitle(link)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/messages/${link.messageId}/link-preview/rename`,
        { newTitle: trimmed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchFiles({ force: true, silent: true });
      setRenamingLink(null);
      setNewLinkTitle('');
    } catch (err) {
      console.error('Error renaming link:', err);
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: err.response?.data || 'Failed to rename link',
      }));
      throw err;
    }
  };

  const handleSaveLinkRename = async (link) => {
    if (!newLinkTitle.trim()) return;
    await handleRenameLink(link, newLinkTitle);
  };

  const handleDeleteLink = async (link) => {
    if (!link?.messageId) return;
    setDeletingLinkId(link.messageId);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/messages/${link.messageId}/link-preview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchFiles({ force: true, silent: true });
    } catch (err) {
      console.error('Error hiding link:', err);
      dispatch(addNotification({
        type: 'error',
        message: err.response?.data || err.message || 'Failed to hide link',
      }));
    } finally {
      setDeletingLinkId(null);
    }
  };

  const handleRenameGroup = async (entry) => {
    const trimmed = (newGroupName || '').trim();
    if (!trimmed) {
      setRenamingGroupId(null);
      setNewGroupName('');
      return;
    }
    if (trimmed === entry.groupName) {
      setRenamingGroupId(null);
      setNewGroupName('');
      return;
    }

    const previousName = entry.groupName;
    const applyLocalName = (name) => {
      setFiles((prev) =>
        prev.map((item) =>
          item.id === entry.id || item.messageId === entry.messageId
            ? { ...item, groupName: name }
            : item
        )
      );
    };

    applyLocalName(trimmed);
    setRenamingGroupId(null);
    setNewGroupName('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API_URL}/messages/${entry.messageId}/resource-group/rename`,
        { groupName: trimmed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const updated = response.data || {};
      const confirmedName = (updated.attachmentGroupName || trimmed).trim();
      applyLocalName(confirmedName);

      const existingMessage = (localMessagesRef.current || []).find(
        (msg) => String(msg?.id || msg?._id) === String(entry.messageId)
      );
      const nextMessage = {
        ...(existingMessage || {}),
        ...updated,
        id: updated.id || entry.messageId,
        channelId: updated.channelId || channelId,
        attachmentGroupName: confirmedName,
        resourceGroupId: updated.resourceGroupId || entry.resourceGroupId || existingMessage?.resourceGroupId,
      };
      if (channelId && nextMessage.id) {
        dispatch(updateMessage({ channelId, message: nextMessage }));
        upsertRealmDocs('Message', [nextMessage]);
      }
    } catch (err) {
      applyLocalName(previousName);
      console.error('Error renaming group:', err);
      const errData = err.response?.data;
      const errMsg = typeof errData === 'string'
        ? errData.replace(/^Error:\s*/i, '')
        : errData?.message || err.message || 'Failed to rename group';
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: errMsg,
      }));
    }
  };

  const downloadFile = async (file) => {
    try {
      await downloadAttachment({
        ...file,
        fileName: collapseDuplicatedFileName(file.fileName),
      });
    } catch (err) {
      console.error('Error downloading file:', err);
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to open file',
      }));
    }
  };

  const downloadGroup = async (group) => {
    if (downloadingGroupId) return;
    setDownloadingGroupId(group.id);
    try {
      await downloadAttachmentsAsZip(group.attachments, group.groupName);
      dispatch(addNotification({
        type: 'success',
        title: 'Download started',
        message: `ZIP with ${group.attachments.length} file${group.attachments.length === 1 ? '' : 's'} from "${group.groupName}"`,
      }));
    } catch (err) {
      console.error('Error downloading group:', err);
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: err?.message || 'Failed to download files as ZIP',
      }));
    } finally {
      setDownloadingGroupId(null);
    }
  };

  const mapAttachmentsForForward = (attachments) =>
    (attachments || []).map(({
      fileName,
      fileUrl,
      fileType,
      fileSize,
      linkCustomTitle,
      linkDescription,
      linkImageUrl,
      linkSiteName,
      linkDomain,
    }) => ({
      fileName,
      fileUrl,
      fileType,
      fileSize: fileSize ?? 0,
      ...(linkCustomTitle != null && { linkCustomTitle }),
      ...(linkDescription != null && { linkDescription }),
      ...(linkImageUrl != null && { linkImageUrl }),
      ...(linkSiteName != null && { linkSiteName }),
      ...(linkDomain != null && { linkDomain }),
    }));

  const handleForwardGroupToChannel = async (entry, targetChannel) => {
    if (!entry || !targetChannel?.id || forwardingGroup) return;
    setForwardingGroup(true);
    try {
      const groupName = (entry.groupName || 'Resource group').trim();
      const attachments = mapAttachmentsForForward(entry.attachments);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      if (attachments.length === 0) {
        await axios.post(
          `${API_URL}/messages/attachments/create-empty-group`,
          { channelId: targetChannel.id, groupName },
          { headers },
        );
      } else {
        await dispatch(sendMessage({
          content: '',
          channelId: targetChannel.id,
          attachments,
          attachmentGroupName: groupName,
        })).unwrap();
      }

      dispatch(setCurrentChannel(targetChannel));

      const isDm = targetChannel.type === 'direct' || targetChannel.type === 'DIRECT_MESSAGE';
      const destName = isDm ? 'direct message' : (targetChannel.name || 'channel');
      dispatch(addNotification({
        type: 'success',
        title: 'Group forwarded',
        message: `"${groupName}" forwarded to ${destName}`,
      }));
      setShowForwardGroupModal(false);
      setGroupToForward(null);
    } catch (err) {
      console.error('Error forwarding group:', err);
      dispatch(addNotification({
        type: 'error',
        title: 'Failed to forward group',
        message: err?.response?.data?.message || err?.message || 'Failed to forward group. Please try again.',
      }));
    } finally {
      setForwardingGroup(false);
    }
  };

  const toggleGroupExpanded = (groupId) => {
    setExpandedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const buildViewerItems = (attachments) =>
    attachments.map((a) => ({
      id: a.id,
      messageId: a.messageId,
      channelId: a.channelId || channelId,
      fileUrl: a.fileUrl,
      fileName: a.fileName,
      fileType: a.fileType,
      fileSize: a.fileSize,
      likedByUserIds: a.likedByUserIds,
      previousFileUrl: a.previousFileUrl,
      previousFileName: a.previousFileName,
      linkCustomTitle: a.linkCustomTitle,
      linkSiteName: a.linkSiteName,
      linkDomain: a.linkDomain,
    }));

  const openGroupViewer = (group, startIndex = 0) => {
    const items = group.attachments || [];
    if (!items.length) {
      dispatch(addNotification({
        type: 'info',
        title: 'Empty group',
        message: 'This group has no items to view.',
      }));
      return;
    }
    setViewingImage({
      images: buildViewerItems(items),
      initialIndex: Math.min(Math.max(startIndex, 0), items.length - 1),
      canRename: canEditGroup(group),
      groupEntry: group,
    });
  };

  const openGroupImage = (group, attachmentIndex) => {
    if (!group.attachments?.length) return;
    const attachment = group.attachments[attachmentIndex];
    if (attachment && isLinkAttachment(attachment)) {
      const href = decodeLinkHref(attachment.fileUrl);
      if (href) {
        window.open(href, '_blank', 'noopener,noreferrer');
      }
      return;
    }
    openGroupViewer(group, attachmentIndex);
  };

  const handleViewerRename = async (item, newName) => {
    if (isLinkAttachment(item)) {
      // Links inside resource groups are attachments, not message link previews.
      if (item.id && item.messageId) {
        await handleRenameAttachment(
          {
            id: item.id,
            messageId: item.messageId,
            fileName: item.fileName,
            fileType: item.fileType,
            linkCustomTitle: item.linkCustomTitle,
          },
          newName
        );
        return;
      }
      await handleRenameLink(
        {
          messageId: item.messageId,
          customTitle: item.linkCustomTitle,
          title: item.fileName,
          url: item.fileUrl,
        },
        newName
      );
      return;
    }
    await handleRenameAttachment(
      {
        id: item.id,
        messageId: item.messageId,
        fileName: item.fileName,
      },
      newName
    );
  };

  const applyGroupFromMessage = (messageDto, groupId) => {
    const incoming = buildFileEntries([messageDto]);
    const group = incoming.find((entry) => entry.type === 'group');
    if (!group) {
      fetchFiles({ force: true, silent: true });
      return;
    }
    setFiles((prev) => {
      let matched = false;
      const next = prev.map((entry) => {
        if (entry.id !== groupId && entry.messageId !== messageDto.id) return entry;
        matched = true;
        return { ...group, id: groupId || group.id };
      });
      return matched ? next : mergeFileEntries(next, incoming);
    });
  };

  const patchMessageAttachments = async (messageId, body) => {
    const token = localStorage.getItem('token');
    const response = await axios.patch(
      `${API_URL}/messages/${messageId}/attachments`,
      body,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  };

  const uploadGroupFiles = async (files) => {
    const token = localStorage.getItem('token');
    const selected = Array.from(files);
    if (!selected.length) {
      throw new Error('No files selected');
    }
    const oversized = selected.filter((f) => f.size > MAX_FILE_SIZE);
    if (oversized.length) {
      throw new Error('Each file must be under 20MB');
    }
    const uploaded = await Promise.all(
      selected.map(async (file) => {
        const formData = new FormData();
        formData.append('file', fileForUpload(file));
        const response = await axios.post(`${API_URL}/files/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        });
        return response.data;
      })
    );
    return uploaded;
  };

  const handleGroupReorder = async (entry, fromIndex, toIndex) => {
    if (fromIndex === toIndex || savingGroupId) return;
    const reordered = [...entry.attachments];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    const attachmentIds = reordered.map((a) => resolveAttachmentId(a)).filter(Boolean);
    if (attachmentIds.length !== reordered.length) {
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: 'Could not reorder — refresh and try again',
      }));
      fetchFiles({ force: true, silent: true });
      return;
    }
    setSavingGroupId(entry.id);
    try {
      const updated = await patchMessageAttachments(entry.messageId, { attachmentIds });
      applyGroupFromMessage(updated, entry.id);
    } catch (err) {
      console.error('Error reordering group:', err);
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: err.response?.data || err.message || 'Failed to reorder images',
      }));
    } finally {
      setSavingGroupId(null);
    }
  };

  const handleGroupRemove = async (entry, index) => {
    if (savingGroupId) return;
    const attachment = entry.attachments[index];
    const attachmentId = resolveAttachmentId(attachment);
    if (!attachmentId) return;
    setSavingGroupId(entry.id);
    try {
      const updated = await patchMessageAttachments(entry.messageId, {
        removeAttachmentIds: [attachmentId],
      });
      if (!updated.attachmentGroupName?.trim() && (updated.attachments || []).length < 2) {
        fetchFiles({ force: true, silent: true });
      } else {
        applyGroupFromMessage(updated, entry.id);
      }
      dispatch(addNotification({
        type: 'success',
        title: 'Updated',
        message: 'Image removed from group',
      }));
    } catch (err) {
      console.error('Error removing from group:', err);
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: err.response?.data || err.message || 'Failed to remove image',
      }));
    } finally {
      setSavingGroupId(null);
    }
  };

  const handleGroupAddFiles = async (entry, files) => {
    if (!files?.length || savingGroupId) return;
    setSavingGroupId(entry.id);
    try {
      const uploaded = await uploadGroupFiles(files);
      const updated = await patchMessageAttachments(entry.messageId, {
        addAttachments: uploaded,
      });
      applyGroupFromMessage(updated, entry.id);
      dispatch(addNotification({
        type: 'success',
        title: 'Updated',
        message: `Added ${uploaded.length} image(s) to group`,
      }));
    } catch (err) {
      console.error('Error adding to group:', err);
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: err.response?.data || err.message || 'Failed to add images',
      }));
    } finally {
      setSavingGroupId(null);
    }
  };

  const handleBrowseAddToGroup = (entry) => {
    activeGroupRef.current = entry;
    fileInputRef.current?.click();
  };

  const handleGroupFileInputChange = (e) => {
    const files = e.target.files;
    const entry = activeGroupRef.current;
    if (files?.length && entry) {
      handleGroupAddFiles(entry, files);
    }
    activeGroupRef.current = null;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSharedDragStart = () => {
    // Defer DOM updates — inserting nodes during dragstart cancels the drag in React.
    window.requestAnimationFrame(() => setIsDraggingShared(true));
  };

  const endSharedDragSession = useCallback(() => {
    setIsDraggingShared(false);
    setIsRootDropTarget(false);
    setDropTargetGroupId(null);
  }, []);

  const handleSharedDragEnd = endSharedDragSession;

  useEffect(() => {
    const onGlobalDragEnd = () => endSharedDragSession();
    document.addEventListener('dragend', onGlobalDragEnd);
    document.addEventListener('drop', onGlobalDragEnd);
    return () => {
      document.removeEventListener('dragend', onGlobalDragEnd);
      document.removeEventListener('drop', onGlobalDragEnd);
    };
  }, [endSharedDragSession]);

  const handleCreateGroupFromItems = async (items, groupName) => {
    const normalized = (items || [])
      .map((item) => ({
        sourceMessageId: item.sourceMessageId,
        attachmentId: resolvePayloadAttachmentId(item) || item.attachmentId,
        fileUrl: item.fileUrl || null,
        itemType: item.itemType || 'attachment',
      }))
      .filter((item) => {
        if (!item.sourceMessageId) return false;
        if (item.itemType === 'link') return true;
        return Boolean(item.attachmentId || item.fileUrl);
      });

    const unique = [];
    const seen = new Set();
    normalized.forEach((item) => {
      const key = item.itemType === 'link'
        ? `${item.sourceMessageId}::link`
        : `${item.sourceMessageId}::${item.attachmentId}`;
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(item);
      }
    });

    if (unique.length < 2 || creatingGroup) return;

    const trimmedName = (groupName || 'Resource group').trim();
    if (!trimmedName) return;

      setCreatingGroup(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/messages/attachments/create-group`,
        {
          channelId,
          groupName: trimmedName,
          items: unique,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const created = response.data;
      const incoming = buildFileEntries([created]);
      const usedMessageIds = new Set(unique.map((item) => String(item.sourceMessageId)));
      const usedAttachmentIds = new Set(
        unique
          .filter((item) => item.itemType !== 'link')
          .map((item) => String(item.attachmentId || ''))
          .filter(Boolean)
      );
      const usedLinkMessages = new Set(
        unique.filter((item) => item.itemType === 'link').map((item) => String(item.sourceMessageId))
      );
      setFiles((prev) => {
        const remaining = (prev || []).filter((entry) => {
          if (entry.type === 'group') return true;
          if (entry.type === 'link') return !usedLinkMessages.has(String(entry.messageId));
          if (!usedMessageIds.has(String(entry.messageId))) return true;
          const aid = resolveAttachmentId(entry);
          return !aid || !usedAttachmentIds.has(String(aid));
        });
        return mergeFileEntries(remaining, incoming);
      });
      if (incoming[0]?.id) {
        setExpandedGroups((prev) => ({ ...prev, [incoming[0].id]: true }));
      }
      dispatch(addNotification({
        type: 'success',
        title: 'Group created',
        message: `"${trimmedName}" created with ${unique.length} items`,
      }));
    } catch (err) {
      console.error('Error creating group:', err);
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: err.response?.data || err.message || 'Failed to create group',
      }));
    } finally {
      setCreatingGroup(false);
      endSharedDragSession();
    }
  };

  const handleCreateEmptyGroup = async () => {
    const trimmedName = createGroupNameInput.trim();
    if (!trimmedName || creatingGroup || !channelId) return;

    setCreatingGroup(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/messages/attachments/create-empty-group`,
        { channelId, groupName: trimmedName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const created = response.data;
      const incoming = buildFileEntries([created]);
      const groupEntry = incoming.find((entry) => entry.type === 'group') || (created?.id ? {
        type: 'group',
        id: created.resourceGroupId ? `group-${created.resourceGroupId}` : `group-${created.id}`,
        resourceGroupId: created.resourceGroupId || null,
        groupName: created.attachmentGroupName || trimmedName,
        attachments: [],
        messageId: created.id,
        channelId: created.channelId || channelId,
        senderId: created.senderId,
        senderName: created.senderName,
        createdAt: created.createdAt || new Date().toISOString(),
      } : null);
      setShowCreateGroupInput(false);
      setCreateGroupNameInput('');
      if (groupEntry) {
        setFiles((prev) => mergeFileEntries([groupEntry], prev));
        setExpandedGroups((prev) => ({ ...prev, [groupEntry.id]: true }));
      }
      dispatch(addNotification({
        type: 'success',
        title: 'Group created',
        message: `"${trimmedName}" is ready — drag items into it`,
      }));
    } catch (err) {
      console.error('Error creating resource group:', err);
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: err.response?.data || err.message || 'Failed to create group',
      }));
    } finally {
      setCreatingGroup(false);
    }
  };

  const openCreateGroupInput = () => {
    setCreateGroupNameInput('');
    setShowCreateGroupInput(true);
  };

  const cancelCreateGroupInput = () => {
    setShowCreateGroupInput(false);
    setCreateGroupNameInput('');
  };

  const handleStandaloneItemDrop = (e, targetFile) => {
    const payload = readSharedAttachmentDragData(e);
    if (!payload || !targetFile?.messageId) return;
    if (isLinkDragPayload(payload) && payload.sourceMessageId === targetFile.messageId) return;
    const targetId = resolveAttachmentId(targetFile);
    const sourceId = resolvePayloadAttachmentId(payload);
    if (!isLinkDragPayload(payload) && (!targetId || !sourceId)) return;
    if (!isLinkDragPayload(payload) && payload.sourceMessageId === targetFile.messageId && sourceId === targetId) {
      return;
    }

    handleCreateGroupFromItems(
      [
        createAttachmentDragPayload(targetFile.messageId, targetId),
        payload,
      ],
      'Resource group'
    );
  };

  const handleMoveToGroup = async (targetEntry, payload, insertIndex = null) => {
    const isLink = isLinkDragPayload(payload);
    const attachmentId = resolvePayloadAttachmentId(payload);
    if (!payload?.sourceMessageId || savingGroupId) return;
    if (!isLink && !attachmentId && !payload?.fileUrl) return;

    if (!isLink && payload.sourceMessageId === targetEntry.messageId) {
      const fromIndex = findAttachmentIndex(targetEntry.attachments, attachmentId);
      if (fromIndex < 0) return;
      let toIndex = insertIndex == null
        ? targetEntry.attachments.length - 1
        : Math.max(0, Math.min(insertIndex, targetEntry.attachments.length - 1));
      if (toIndex > fromIndex) toIndex -= 1;
      if (fromIndex !== toIndex) {
        handleGroupReorder(targetEntry, fromIndex, toIndex);
      }
      return;
    }

    setSavingGroupId(targetEntry.id);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/messages/${targetEntry.messageId}/attachments/move`,
        buildMoveRequestBody(payload, insertIndex),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const target = response.data?.target;
      const source = response.data?.source;
      const isLink = isLinkDragPayload(payload);
      const attachmentId = resolvePayloadAttachmentId(payload);
      setFiles((prev) => {
        let next = (prev || []).filter((entry) => {
          if (entry.type === 'group') return true;
          if (String(entry.messageId) !== String(payload.sourceMessageId)) return true;
          if (isLink && entry.type === 'link') return false;
          if (!isLink && (String(resolveAttachmentId(entry)) === String(attachmentId) || String(entry.id) === String(attachmentId))) {
            return false;
          }
          return true;
        }).map((entry) => {
          if (entry.type !== 'group' || String(entry.messageId) !== String(payload.sourceMessageId)) {
            return entry;
          }
          return {
            ...entry,
            attachments: (entry.attachments || []).filter(
              (att) => String(resolveAttachmentId(att)) !== String(attachmentId)
            ),
          };
        });
        if (source == null) {
          next = next.filter((entry) => {
            if (entry.type !== 'group' || String(entry.messageId) !== String(payload.sourceMessageId)) {
              return true;
            }
            return (entry.attachments || []).length > 0 || Boolean(entry.resourceGroupId);
          });
        }
        const incoming = buildFileEntries([target, source].filter(Boolean));
        return mergeFileEntries(next, incoming);
      });
      setExpandedGroups((prev) => ({ ...prev, [targetEntry.id]: true }));
    } catch (err) {
      console.error('Error moving image to group:', err);
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: err.response?.data || err.message || 'Failed to move image into group',
      }));
    } finally {
      setSavingGroupId(null);
      setDropTargetGroupId(null);
      endSharedDragSession();
    }
  };

  const handleDetachToRoot = async (payload) => {
    const attachmentId = resolvePayloadAttachmentId(payload);
    if (!payload?.sourceMessageId || !attachmentId || savingGroupId) return;

    const sourceGroup = findGroupEntryByMessageId(payload.sourceMessageId);
    if (!sourceGroup || !canEditGroup(sourceGroup)) return;

    setSavingGroupId(sourceGroup.id);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/messages/${payload.sourceMessageId}/attachments/${attachmentId}/detach`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchFiles({ force: true, silent: true });
    } catch (err) {
      console.error('Error moving image out of group:', err);
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: err.response?.data || err.message || 'Failed to move image out of group',
      }));
    } finally {
      setSavingGroupId(null);
      setIsRootDropTarget(false);
      endSharedDragSession();
    }
  };

  const handleTouchHighlight = useCallback((drop) => {
    if (isRootDropTarget(drop)) {
      setDropTargetGroupId(null);
      setIsRootDropTarget(true);
      return;
    }
    if (!drop?.messageId) {
      setDropTargetGroupId(null);
      setIsRootDropTarget(false);
      return;
    }
    const entry = findGroupEntryByMessageId(drop.messageId);
    setDropTargetGroupId(entry?.id ?? null);
    setIsRootDropTarget(false);
  }, [files]);

  const handleTouchAttachmentDrop = useCallback((payload, drop) => {
    if (isRootDropTarget(drop)) {
      handleDetachToRoot(payload);
      return;
    }

    if (!payload?.sourceMessageId || savingGroupId) return;
    const isLink = isLinkDragPayload(payload);
    const attachmentId = resolvePayloadAttachmentId(payload);
    if (!isLink && !attachmentId) return;

    const targetGroup = findGroupEntryByMessageId(drop.messageId);
    if (!targetGroup || !canEditGroup(targetGroup)) return;

    if (!isLink && payload.sourceMessageId === drop.messageId) {
      const fromIndex = findAttachmentIndex(targetGroup.attachments, attachmentId);
      if (fromIndex < 0) return;
      let toIndex = drop.append
        ? targetGroup.attachments.length
        : (drop.insertIndex ?? targetGroup.attachments.length);
      if (toIndex > fromIndex) toIndex -= 1;
      if (fromIndex !== toIndex) {
        handleGroupReorder(targetGroup, fromIndex, toIndex);
      }
      return;
    }

    const insertIndex = drop.append ? null : drop.insertIndex;
    handleMoveToGroup(targetGroup, payload, insertIndex);
  }, [files, savingGroupId]);

  const handleGroupDragOver = (e, entry) => {
    if (!canEditGroup(entry) || !isSharedAttachmentDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    setIsRootDropTarget(false);
    setDropTargetGroupId(entry.id);
  };

  const handleGroupDragLeave = (e, entry) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDropTargetGroupId((prev) => (prev === entry.id ? null : prev));
    }
  };

  const handleGroupDrop = (e, entry) => {
    if (!canEditGroup(entry)) return;
    e.preventDefault();
    e.stopPropagation();
    const payload = readSharedAttachmentDragData(e);
    if (payload) {
      handleMoveToGroup(entry, payload);
    }
    endSharedDragSession();
  };

  const handleRootDragOver = (e) => {
    if (!isSharedAttachmentDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDropTargetGroupId(null);
    setIsRootDropTarget(true);
  };

  const handleRootDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsRootDropTarget(false);
    }
  };

  const handleRootDrop = (e) => {
    if (e.currentTarget.dataset.sharedFilesDrop !== SHARED_FILES_ROOT_DROP) return;
    e.preventDefault();
    e.stopPropagation();
    if (rootDropHandledRef.current) return;
    rootDropHandledRef.current = true;
    window.setTimeout(() => {
      rootDropHandledRef.current = false;
    }, 0);

    const payload = readSharedAttachmentDragData(e);
    if (payload) {
      handleDetachToRoot(payload);
    }
    endSharedDragSession();
  };

  const getFileIcon = (file) => (
    <FileTypeIcon
      attachment={file}
      fileType={file?.fileType}
      size="lg"
      className="!w-full !h-full !max-w-[3rem] !max-h-[3rem] !rounded-lg"
    />
  );

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const toggleTypeFilter = (key) => {
    setTypeFilters((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const anyTypeFilterActive = SHARED_FILE_FILTER_KEYS.some((key) => typeFilters[key]);

  const attachmentMatchesSearch = (attachment, q) => {
    if (!q) return true;
    if (isLinkAttachment(attachment)) {
      const title = getLinkDisplayTitle({
        title: attachment.fileName,
        customTitle: attachment.linkCustomTitle,
        url: attachment.fileUrl,
      });
      return (
        title.toLowerCase().includes(q)
        || (attachment.fileName || '').toLowerCase().includes(q)
        || (attachment.fileUrl || '').toLowerCase().includes(q)
      );
    }
    return (attachment.fileName || '').toLowerCase().includes(q);
  };

  const getVisibleGroupAttachments = (entry, q) => {
    const attachments = entry.attachments || [];
    if (!q) return attachments;
    return attachments.filter((att) => attachmentMatchesSearch(att, q));
  };

  const entryMatchesSearch = (entry, q) => {
    if (!q) return true;
    if (entry.type === 'group') {
      if ((entry.groupName || '').toLowerCase().includes(q)) return true;
      return (entry.attachments || []).some((att) => attachmentMatchesSearch(att, q));
    }
    if (entry.type === 'link') {
      return (
        getLinkDisplayTitle(entry).toLowerCase().includes(q)
        || entry.title?.toLowerCase().includes(q)
        || entry.customTitle?.toLowerCase().includes(q)
        || entry.url?.toLowerCase().includes(q)
        || entry.siteName?.toLowerCase().includes(q)
        || entry.domain?.toLowerCase().includes(q)
      );
    }
    return (entry.fileName || '').toLowerCase().includes(q);
  };

  const groupExpandsFromSearch = (entry, q) => {
    if (!q || entry.type !== 'group') return false;
    return getVisibleGroupAttachments(entry, q).length > 0;
  };

  // Perform filtering in memory (search + type + liked)
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const currentUserId = currentUser?.id;
  const filteredFiles = files.filter((entry) => {
    if (!entryMatchesSearch(entry, normalizedSearch)) return false;
    const category = getSharedEntryCategory(entry);
    if (typeFilters[category]) return true;
    if (typeFilters.liked && sharedEntryMatchesLikedFilter(entry, currentUserId)) return true;
    return false;
  });

  return (
    <div className="flex flex-col h-full min-h-0 space-y-4">
      {/* Search Bar */}
      <div className="px-1 py-1 flex-shrink-0">
        <div className="relative rounded-xl shadow-sm">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <svg className="h-4 w-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            name="search"
            id="search-files"
            className="block w-full rounded-xl border-gray-200 dark:border-gray-700 pl-9 pr-3 py-2 text-xs bg-gray-50 dark:bg-gray-800/50 text-gray-900 dark:text-white placeholder-gray-500 focus:border-primary-500 focus:ring-primary-500 focus:bg-white dark:focus:bg-gray-800 transition-all border border-solid focus:outline-none"
            placeholder="Search resources, files, groups, or links..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery('');
              }}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Type filters */}
      <div className="px-1 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
            {[
              { key: 'images', label: 'Images' },
              { key: 'files', label: 'Files' },
              { key: 'links', label: 'Links' },
              { key: 'others', label: 'Others' },
              { key: 'groups', label: 'Groups' },
              { key: 'liked', label: 'Liked' },
            ].map(({ key, label }) => {
              const checked = typeFilters[key];
              return (
                <label
                  key={key}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[11px] font-medium cursor-pointer transition-colors select-none ${
                    checked
                      ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 dark:border-primary-600'
                      : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 h-3 w-3"
                    checked={checked}
                    onChange={() => toggleTypeFilter(key)}
                  />
                  {label}
                </label>
              );
            })}
          </div>
          <button
            type="button"
            onClick={openCreateGroupInput}
            disabled={creatingGroup}
            className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-primary-500 bg-primary-50 text-primary-700 hover:bg-primary-100 dark:bg-primary-900/30 dark:text-primary-300 dark:border-primary-600 dark:hover:bg-primary-900/50 disabled:opacity-50 transition-colors flex-shrink-0 ml-auto"
            title="Create new group"
            aria-label="Create new group"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>

        {showCreateGroupInput && (
          <div className="mt-2 flex items-center gap-2">
            <input
              type="text"
              value={createGroupNameInput}
              onChange={(e) => setCreateGroupNameInput(e.target.value)}
              placeholder="New group name"
              maxLength={80}
              disabled={creatingGroup}
              className="flex-1 min-w-0 px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 disabled:opacity-50"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateEmptyGroup();
                if (e.key === 'Escape' && !creatingGroup) cancelCreateGroupInput();
              }}
            />
            <button
              type="button"
              disabled={creatingGroup || !createGroupNameInput.trim()}
              onClick={handleCreateEmptyGroup}
              className="flex-shrink-0 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {creatingGroup ? 'Creating…' : 'Create'}
            </button>
            <button
              type="button"
              disabled={creatingGroup}
              onClick={cancelCreateGroupInput}
              className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
              title="Cancel"
              aria-label="Cancel create group"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Body Area */}
      <div className="flex-1 overflow-y-auto min-h-0 min-w-0 pr-1">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : error ? (
          <div className="text-center py-8 text-red-500 text-sm">
            {error}
          </div>
        ) : filteredFiles.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              {!anyTypeFilterActive
                ? 'Select at least one filter to show items'
                : searchQuery
                  ? `No resources match "${searchQuery}"`
                  : 'No shared resources yet'}
            </p>
          </div>
        ) : (
          <div className="space-y-4 rounded-lg">
            {isDraggingShared && (
              <div className="sticky top-0 z-10 space-y-2 mb-3">
                <div
                  data-shared-files-drop={SHARED_FILES_ROOT_DROP}
                  data-drop-append="true"
                  className={`rounded-lg border-2 border-dashed px-3 py-2 text-center transition-colors ${
                    isRootDropTarget
                      ? 'border-primary-500 bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-200 ring-2 ring-primary-400'
                      : 'border-primary-300 bg-primary-50/80 dark:bg-primary-900/15 text-primary-600 dark:text-primary-300'
                  }`}
                  onDragOver={handleRootDragOver}
                  onDragLeave={handleRootDragLeave}
                  onDrop={handleRootDrop}
                >
                  <span className="text-[11px] font-medium">
                    Drop here to move item out of group
                  </span>
                </div>
              </div>
            )}
          <div className="space-y-4">
              {filteredFiles.map((entry, index) => {
                if (entry.type === 'group') {
                  const visibleAttachments = getVisibleGroupAttachments(entry, normalizedSearch);
                  const isSearchFiltered = Boolean(normalizedSearch);
                  const isExpanded = !!expandedGroups[entry.id] || groupExpandsFromSearch(entry, normalizedSearch);
                  const isDownloading = downloadingGroupId === entry.id;
                  const groupHasItems = entry.attachments.length > 0;
                  const displayItemCount = isSearchFiltered ? visibleAttachments.length : entry.attachments.length;

                  const openGroupAttachment = (attachment) => {
                    const originalIndex = findAttachmentIndex(entry.attachments, resolveAttachmentId(attachment));
                    openGroupImage(entry, originalIndex >= 0 ? originalIndex : 0);
                  };
                
                return (
                    <div
                      key={entry.id}
                      data-shared-files-drop={entry.messageId}
                      data-drop-append="true"
                      className={`p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors border ${
                        dropTargetGroupId === entry.id
                          ? 'border-primary-500 bg-primary-50/50 dark:bg-primary-900/20 ring-2 ring-primary-300/40'
                          : 'border-gray-100 dark:border-gray-700/60'
                      }`}
                      onDragOver={canEditGroup(entry) ? (e) => handleGroupDragOver(e, entry) : undefined}
                      onDragLeave={canEditGroup(entry) ? (e) => handleGroupDragLeave(e, entry) : undefined}
                      onDrop={canEditGroup(entry) ? (e) => handleGroupDrop(e, entry) : undefined}
                    >
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => toggleGroupExpanded(entry.id)}
                          className="flex-shrink-0 p-1 rounded-md text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-200/70 dark:hover:bg-gray-700 transition-colors"
                          title={isExpanded ? 'Collapse group' : 'Expand group'}
                          aria-label={isExpanded ? 'Collapse group' : 'Expand group'}
                          aria-expanded={isExpanded}
                        >
                          <svg
                            className={`w-4 h-4 transition-transform ${isExpanded ? '' : '-rotate-90'}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>

                        <div className="flex-shrink-0 p-1.5 rounded-md bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-300">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>

                        <div className="flex-1 min-w-0">
                          {renamingGroupId === entry.id ? (
                            <div className="flex items-center gap-1">
                              <input
                                type="text"
                                value={newGroupName}
                                onChange={(e) => setNewGroupName(e.target.value)}
                                className="flex-1 min-w-0 px-2 py-0.5 text-xs rounded border border-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500"
                                autoFocus
                                maxLength={80}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleRenameGroup(entry);
                                  if (e.key === 'Escape') {
                                    setRenamingGroupId(null);
                                    setNewGroupName('');
                                  }
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => handleRenameGroup(entry)}
                                className="p-0.5 text-green-600 hover:text-green-700"
                                title="Save group name"
                                aria-label="Save group name"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setRenamingGroupId(null);
                                  setNewGroupName('');
                                }}
                                className="p-0.5 text-red-600 hover:text-red-700"
                                title="Cancel"
                                aria-label="Cancel rename"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 min-w-0">
                              <h4
                                className="text-xs font-semibold text-gray-900 dark:text-white truncate"
                                title={entry.groupName}
                              >
                                {entry.groupName}
                              </h4>
                              {canEditGroup(entry) && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setRenamingGroupId(entry.id);
                                    setNewGroupName(entry.groupName || '');
                                  }}
                                  className="flex-shrink-0 p-0.5 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                                  title="Rename group"
                                  aria-label={`Rename ${entry.groupName}`}
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          )}
                          {renamingGroupId !== entry.id && (
                            <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-gray-500 dark:text-gray-400">
                              <span>
                                {displayItemCount} item{displayItemCount === 1 ? '' : 's'}
                                {isSearchFiltered && displayItemCount !== entry.attachments.length
                                  ? ` of ${entry.attachments.length}`
                                  : ''}
                              </span>
                              <span>•</span>
                              <span className="truncate max-w-[80px]" title={entry.senderName}>{entry.senderName}</span>
                              <span>•</span>
                              <span>{new Date(entry.createdAt).toLocaleDateString()}</span>
                            </div>
                          )}
                        </div>

                        {groupHasItems && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openGroupViewer(entry, 0);
                            }}
                            className="flex-shrink-0 p-1.5 rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            title={`View items in ${entry.groupName}`}
                            aria-label={`View items in ${entry.groupName}`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setGroupToForward(entry);
                            setShowForwardGroupModal(true);
                          }}
                          disabled={forwardingGroup}
                          className="flex-shrink-0 p-1.5 rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
                          title={`Forward ${entry.groupName}`}
                          aria-label={`Forward ${entry.groupName}`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                          </svg>
                        </button>
                        <ResourceBookmarkButton
                          messageId={entry.messageId}
                          bookmarked={isResourceBookmarked(entry.messageId)}
                          busy={bookmarkBusyId === entry.messageId}
                          onToggle={handleToggleResourceBookmark}
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadGroup(entry);
                          }}
                          disabled={isDownloading}
                          className="flex-shrink-0 p-1.5 rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
                          title={isDownloading ? 'Preparing ZIP…' : `Download all files as ZIP`}
                          aria-label={isDownloading ? 'Preparing ZIP download' : `Download all files in ${entry.groupName} as ZIP`}
                        >
                          {isDownloading ? (
                            <span className="inline-block w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                          )}
                        </button>
                      </div>

                      {isExpanded && (
                        <div className="mt-3 pl-1">
                          {isSearchFiltered && visibleAttachments.length === 0 ? (
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 py-2">
                              No files in this group match your search.
                            </p>
                          ) : canEditGroup(entry) && !isSearchFiltered ? (
                            <GroupImageComposer
                              attachments={entry.attachments}
                              messageId={entry.messageId}
                              editable={!savingGroupId}
                              isDropTarget={dropTargetGroupId === entry.id}
                              onReorder={(from, to) => handleGroupReorder(entry, from, to)}
                              onRemove={(index) => handleGroupRemove(entry, index)}
                              onAddFiles={(files) => handleGroupAddFiles(entry, files)}
                              onBrowseAdd={() => handleBrowseAddToGroup(entry)}
                              onExternalDrop={(payload, insertIndex) =>
                                handleMoveToGroup(entry, payload, insertIndex)
                              }
                              onImageClick={(index) => openGroupImage(entry, index)}
                              onTouchAttachmentDrop={handleTouchAttachmentDrop}
                              onTouchHighlight={handleTouchHighlight}
                              onSharedDragStart={handleSharedDragStart}
                              onSharedDragEnd={handleSharedDragEnd}
                              canRename={canEditGroup(entry)}
                              onRename={handleRenameAttachment}
                              renderFileIcon={getFileIcon}
                              hint={
                                savingGroupId === entry.id
                                  ? 'Saving…'
                                  : entry.attachments.length === 0
                                    ? 'Drag files, images, or links here'
                                    : undefined
                              }
                            />
                          ) : canEditGroup(entry) && isSearchFiltered ? (
                            <GroupImageComposer
                              attachments={visibleAttachments}
                              messageId={entry.messageId}
                              editable={false}
                              onImageClick={(index) => openGroupAttachment(visibleAttachments[index])}
                              onTouchAttachmentDrop={handleTouchAttachmentDrop}
                              onTouchHighlight={handleTouchHighlight}
                              canRename={false}
                              onRename={handleRenameAttachment}
                              renderFileIcon={getFileIcon}
                            />
                          ) : (
                            <div className="flex flex-wrap gap-2">
                              {visibleAttachments.map((file, fileIndex) => (
                                <FileHoverPreview
                                  key={file.id || `${entry.id}-${fileIndex}`}
                                  attachment={file}
                                  className="inline-block"
                                  canRename={canEditGroup(entry) && !isSearchFiltered}
                                  onRename={handleRenameAttachment}
                                >
                                  <div className="relative inline-block">
                                    <PresignedImage
                                      fileKey={file.fileUrl}
                                      originalName={file.fileName}
                                      alt={file.fileName}
                                      className="w-[100px] h-[100px] object-cover rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm cursor-pointer hover:opacity-90 transition-opacity"
                                      onClick={() => openGroupAttachment(file)}
                                    />
                                    <AttachmentLikeHeart
                                      attachment={file}
                                      userId={currentUserId}
                                      size="sm"
                                      className="absolute bottom-1.5 right-1.5 z-[2]"
                                    />
                                  </div>
                                </FileHoverPreview>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                }

                if (entry.type === 'link') {
                  const link = entry;
                  const href = decodeLinkHref(link.url);
                  const displayTitle = getLinkDisplayTitle(link);
                  const canRenameLink = canEditLink(link);
                  const isRenamingLink = renamingLink === linkKey(link);
                  const isDeletingLink = deletingLinkId === link.messageId;

                  return (
                    <LinkHoverPreview
                      key={link.id}
                      link={link}
                      href={href}
                      className="block"
                      canRename={canRenameLink}
                      onRename={handleRenameLink}
                    >
                      <div className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group/link">
                        <DraggableLinkThumb
                          link={link}
                          href={href}
                          canDrag={canRenameLink}
                          onTouchAttachmentDrop={handleTouchAttachmentDrop}
                          onTouchHighlight={handleTouchHighlight}
                          onSharedDragStart={handleSharedDragStart}
                          onSharedDragEnd={handleSharedDragEnd}
                        >
                          <div className="relative flex-shrink-0">
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block w-12 h-12 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 overflow-hidden flex items-center justify-center"
                          >
                            {link.imageUrl ? (
                              <img
                                src={link.imageUrl}
                                alt=""
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                              <svg className="w-7 h-7 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                              </svg>
                            )}
                          </a>
                        </div>
                        </DraggableLinkThumb>
                        <div className="flex-1 min-w-0">
                          {isRenamingLink ? (
                            <div className="flex items-center space-x-2">
                              <div className="flex flex-1 min-w-0 items-center border border-primary-500 rounded overflow-hidden bg-white dark:bg-gray-800">
                                <input
                                  type="text"
                                  value={newLinkTitle}
                                  onChange={(e) => setNewLinkTitle(e.target.value)}
                                  className="flex-1 min-w-0 px-2 py-1 text-xs focus:outline-none bg-transparent text-gray-900 dark:text-white"
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveLinkRename(link);
                                    if (e.key === 'Escape') {
                                      setRenamingLink(null);
                                      setNewLinkTitle('');
                                    }
                                  }}
                                />
                              </div>
                              <button type="button" onClick={() => handleSaveLinkRename(link)} className="text-green-600 hover:text-green-700 p-0.5">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setRenamingLink(null);
                                  setNewLinkTitle('');
                                }}
                                className="text-red-600 hover:text-red-700 p-0.5"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between gap-1 min-w-0">
                              <a
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-1 min-w-0 text-xs font-semibold text-gray-900 dark:text-white truncate group-hover:text-primary-600 dark:group-hover:text-primary-400"
                                title={displayTitle}
                              >
                                {displayTitle}
                              </a>
                              <div className="flex items-center flex-shrink-0 gap-0.5">
                              {canRenameLink && (
                                <div className="flex items-center opacity-0 group-hover/link:opacity-100 transition-opacity">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setRenamingLink(linkKey(link));
                                      setNewLinkTitle(displayTitle);
                                    }}
                                    className="p-0.5 text-gray-400 hover:text-primary-600 transition-colors"
                                    title="Rename link"
                                    aria-label="Rename link"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                    </svg>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleDeleteLink(link);
                                    }}
                                    disabled={isDeletingLink}
                                    className="p-0.5 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                                    title="Hide from Shared Resources"
                                    aria-label="Hide from Shared Resources"
                                  >
                                    {isDeletingLink ? (
                                      <span className="inline-block w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    )}
                                  </button>
                    </div>
                              )}
                              <ResourceBookmarkButton
                                messageId={link.messageId}
                                bookmarked={isResourceBookmarked(link.messageId)}
                                busy={bookmarkBusyId === link.messageId}
                                onToggle={handleToggleResourceBookmark}
                                compact
                              />
                              </div>
                            </div>
                          )}
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-[10px] text-primary-600 dark:text-primary-400 truncate mt-0.5"
                            title={href}
                          >
                            {link.siteName || link.domain || href}
                          </a>
                          {link.description && !isRenamingLink && (
                            <p className="text-[10px] text-gray-500 dark:text-gray-400 line-clamp-2 mt-0.5">
                              {link.description}
                            </p>
                          )}
                          <div className="flex items-center gap-1.5 mt-1 text-[10px] text-gray-500 dark:text-gray-400">
                            <span className="truncate max-w-[80px]" title={link.senderName}>{link.senderName}</span>
                            <span>•</span>
                            <span>{new Date(link.createdAt).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                    </LinkHoverPreview>
                  );
                }

                const file = entry;
                const canRename = canEditFile(file);
                const { extension: fileExtension } = splitFileNameParts(file.fileName);
                
                return (
                  <div key={file.id || `${file.messageId}-${index}`} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group/file">
                    <DraggableFileThumb
                      file={file}
                      showGrip
                      canDrag={canRename}
                      canRename={canRename}
                      onRename={handleRenameAttachment}
                      onView={canPreviewInApp(file) ? () => setViewingImage({ images: [file], initialIndex: 0, canRename: canEditFile(file) }) : undefined}
                      onDownload={() => downloadFile(file)}
                      renderIcon={getFileIcon}
                      onStandaloneDrop={handleStandaloneItemDrop}
                      onTouchAttachmentDrop={handleTouchAttachmentDrop}
                      onTouchHighlight={handleTouchHighlight}
                      onSharedDragStart={handleSharedDragStart}
                      onSharedDragEnd={handleSharedDragEnd}
                    />
                    
                    <div className="flex-1 min-w-0">
                      {renamingFile === sharedFileKey(file) ? (
                        <div className="flex items-center space-x-2">
                          <div className="flex flex-1 min-w-0 items-center border border-primary-500 rounded overflow-hidden bg-white dark:bg-gray-800">
                          <input
                            type="text"
                            value={newFileName}
                            onChange={(e) => setNewFileName(e.target.value)}
                              className="flex-1 min-w-0 px-2 py-1 text-xs focus:outline-none bg-transparent text-gray-900 dark:text-white"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleRename(file);
                              if (e.key === 'Escape') setRenamingFile(null);
                            }}
                          />
                            {fileExtension && (
                              <span className="flex-shrink-0 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 border-l border-primary-500/30 select-none">
                                {fileExtension.replace(/^\./, '')}
                              </span>
                            )}
                          </div>
                          <button onClick={() => handleRename(file)} className="text-green-600 hover:text-green-700 p-0.5">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                          <button onClick={() => setRenamingFile(null)} className="text-red-600 hover:text-red-700 p-0.5">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center justify-between gap-1 min-w-0">
                            <h4 
                              className="flex-1 min-w-0 text-xs font-semibold cursor-pointer hover:text-primary-600 dark:hover:text-primary-400"
                              onClick={() => {
                                if (canPreviewInApp(file)) {
                                  setViewingImage({
                                    images: [file],
                                    initialIndex: 0,
                                    canRename: canEditFile(file),
                                  });
                                  return;
                                }
                                downloadFile(file);
                              }}
                            >
                              <FileNameWithExtension
                                fileName={file.fileName}
                                nameClassName="text-gray-900 dark:text-white font-semibold"
                              />
                            </h4>
                            {canRename && (
                              <button 
                                onClick={() => {
                                  setRenamingFile(sharedFileKey(file));
                                  setNewFileName(splitFileNameParts(file.fileName).baseName);
                                }}
                                className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-400 hover:text-primary-600 transition-all ml-1 flex-shrink-0"
                                title="Rename file"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                              </button>
                            )}
                            <ResourceBookmarkButton
                              messageId={file.messageId}
                              bookmarked={isResourceBookmarked(file.messageId)}
                              busy={bookmarkBusyId === file.messageId}
                              onToggle={handleToggleResourceBookmark}
                              compact
                            />
                          </div>
                          <div className="flex items-center space-x-1.5 mt-0.5 text-[10px] text-gray-500 dark:text-gray-400">
                            <span>{formatFileSize(file.fileSize)}</span>
                            <span>•</span>
                            <span className="truncate max-w-[80px]" title={file.senderName}>{file.senderName}</span>
                            <span>•</span>
                            <span>{new Date(file.createdAt).toLocaleDateString()}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
            </div>

      {!loading && !error && (
        <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 px-4 py-2.5">
                <button
                  type="button"
            onClick={handleLoadMore}
            disabled={!hasMoreFiles || loadingMore}
            className="w-full rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-[13px] font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/80 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white dark:disabled:hover:bg-gray-800 transition-colors"
          >
            {loadingMore
              ? `Loading next ${RESOURCES_PAGE_SIZE} resources…`
              : hasMoreFiles
                ? 'Load more'
                : 'No more resources'}
                </button>
              </div>
            )}

      {viewingImage && (
        <ImageViewerModal
          images={viewingImage.images}
          initialIndex={viewingImage.initialIndex || 0}
          imageUrl={viewingImage.url}
          fileName={viewingImage.fileName}
          canRename={viewingImage.canRename}
          onRename={viewingImage.canRename ? handleViewerRename : undefined}
          onClose={() => setViewingImage(null)}
          onDownload={downloadFile}
        />
      )}

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept="*/*"
        multiple
        onChange={handleGroupFileInputChange}
      />

      <ForwardMessageModal
        isOpen={showForwardGroupModal}
        onClose={() => {
          if (!forwardingGroup) {
            setShowForwardGroupModal(false);
            setGroupToForward(null);
          }
        }}
        groupToForward={groupToForward}
        excludeChannelId={channelId}
        onChannelSelected={(targetChannel) => {
          if (groupToForward) {
            return handleForwardGroupToChannel(groupToForward, targetChannel);
          }
          return undefined;
        }}
      />
    </div>
  );
};

export default SharedFilesList;
